import React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Button, Chip, SearchField, Skeleton, Typography } from '@heroui/react';
import { getNodeTestIdProp, useIsNarrowViewport } from '@/utils';

type ProductsToolbarProps = {
    search: string;
    onSearchChange: (value: string) => void;
    /** Applied filter groups; badges the Filters button when above zero. */
    filterCount: number;
    onOpenFilters: () => void;
    /** `null` until the first page arrives. */
    totalCount: number | null;
    /**
     * The catalogue the count is out of, while a search or filter narrows the
     * list: the count then reads "33 of 1,488 products". `null` otherwise.
     */
    catalogCount: number | null;
    /**
     * Export Excel (bos-app#120), a section of its own that the page mounts
     * here, between the count and Add product.
     */
    exportAction: React.ReactNode;
    onCreate: () => void;
};

/** The count's place in the row: a line of its own on phones (see below). */
const COUNT_LAYOUT = 'order-last w-full sm:order-none sm:ml-auto sm:w-auto';

/**
 * The Products toolbar (BOS-199): search, the Filters button that opens the
 * drawer, the result count, Export Excel (bos-app#120) and Add product.
 *
 * On phones it stacks: the search takes the first line, Filters, Export and a
 * round Add share the second, and the count drops to a line of its own.
 * `order` moves the count; the DOM keeps the desktop reading order.
 */
export const ProductsToolbar: React.FC<ProductsToolbarProps> = ({
    search,
    onSearchChange,
    filterCount,
    onOpenFilters,
    totalCount,
    catalogCount,
    exportAction,
    onCreate,
}) => {
    const { t } = useTranslation('pages');
    const isNarrow = useIsNarrowViewport();

    const copy = React.useMemo(
        () => ({
            searchPlaceholder: t(
                'products.components.productTable.searchPlaceholder',
            ),
            searchAria: t('products.components.productTable.searchAria'),
            filters: t('products.components.productTable.filters.button'),
            filtersAria: t(
                'products.components.productTable.filters.buttonAria',
                { count: filterCount },
            ),
            resultSummary:
                catalogCount === null
                    ? t('products.components.productTable.resultSummary', {
                          count: totalCount ?? 0,
                      })
                    : t(
                          'products.components.productTable.resultSummaryOfCatalog',
                          { count: totalCount ?? 0, total: catalogCount },
                      ),
            addProduct: t('products.components.productTable.addProduct'),
        }),
        [catalogCount, filterCount, t, totalCount],
    );

    return (
        <div
            className="flex flex-wrap items-center gap-3"
            {...getNodeTestIdProp('product-table-toolbar')}
        >
            <SearchField
                aria-label={copy.searchAria}
                className="w-full sm:w-80"
                onChange={onSearchChange}
                value={search}
                {...getNodeTestIdProp('product-table-search')}
            >
                <SearchField.Group>
                    <SearchField.SearchIcon />
                    <SearchField.Input placeholder={copy.searchPlaceholder} />
                    <SearchField.ClearButton />
                </SearchField.Group>
            </SearchField>
            <Button
                aria-label={filterCount > 0 ? copy.filtersAria : undefined}
                onPress={onOpenFilters}
                variant="secondary"
                {...getNodeTestIdProp('product-table-filters')}
            >
                <Icon aria-hidden icon="gravity-ui:funnel" />
                {copy.filters}
                {filterCount > 0 ? (
                    <Chip
                        color="accent"
                        size="sm"
                        variant="soft"
                        {...getNodeTestIdProp('product-table-filters-count')}
                    >
                        <Chip.Label>{filterCount}</Chip.Label>
                    </Chip>
                ) : null}
            </Button>
            {totalCount === null ? (
                <div className={COUNT_LAYOUT}>
                    <Skeleton className="h-4 w-24 rounded-md" />
                </div>
            ) : (
                <Typography
                    className={COUNT_LAYOUT}
                    color="muted"
                    type="body-sm"
                    {...getNodeTestIdProp('product-table-result-summary')}
                >
                    {copy.resultSummary}
                </Typography>
            )}
            {exportAction}
            <Button
                aria-label={isNarrow ? copy.addProduct : undefined}
                className="ml-auto sm:ml-0"
                isIconOnly={isNarrow}
                onPress={onCreate}
                variant="primary"
                {...getNodeTestIdProp('product-table-add')}
            >
                <Icon aria-hidden icon="gravity-ui:plus" />
                {isNarrow ? null : copy.addProduct}
            </Button>
        </div>
    );
};
